import { useCallback, useEffect, useState } from "react";
import { Mail, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, waLink } from "@/lib/api";
import { QUOTE_STATUS } from "@/lib/services";

function QuoteCard({ q, onStatus }) {
  const s = QUOTE_STATUS[q.status];
  const v = q.vehicle || {};
  const text = `[AutoTech Pro] Gentile ${q.name}, in merito alla sua richiesta di preventivo #${q.number} (${q.service_label})`;
  return (
    <div className="surface p-5 fade-up" data-testid={`quote-card-${q.number}`}>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <span className="mono text-[#E50914] font-bold">#{q.number}</span>
        <span className="font-semibold text-white">{q.service_label}</span>
        <span className={`st ${s.cls}`} data-testid={`quote-status-${q.number}`}>{s.label}</span>
        <span className="text-xs text-slate-500 ml-auto mono">{new Date(q.created_at).toLocaleString("it-IT")}</span>
      </div>
      <div className="text-sm text-slate-300">{q.name} · {q.region} · <span className="mono">{q.phone}</span> · {q.email}</div>
      {(v.make || v.plate) && <div className="text-sm text-slate-400 mt-1">{[v.make, v.model].filter(Boolean).join(" ")} {v.plate && <span className="tag ml-1">{v.plate}</span>}</div>}
      <p className="text-sm text-slate-400 mt-3 whitespace-pre-wrap">{q.description}</p>
      <div className="text-xs text-slate-500 mt-2">{q.estimate}</div>
      <div className="flex flex-wrap gap-2 mt-4">
        <a href={waLink(q.phone, text)} target="_blank" rel="noreferrer" className="btn-wa px-3 py-2 text-xs flex items-center gap-1.5" data-testid={`quote-wa-${q.number}`}>
          <MessageCircle size={14} /> Rispondi su WhatsApp
        </a>
        <a href={`mailto:${q.email}?subject=${encodeURIComponent(`Preventivo #${q.number} - ${q.service_label}`)}`} className="btn-ghost px-3 py-2 text-xs flex items-center gap-1.5" data-testid={`quote-mail-${q.number}`}>
          <Mail size={14} /> Rispondi via email
        </a>
        <select className="field !w-auto !py-1.5 !text-xs" value={q.status} onChange={(e) => onStatus(q, e.target.value)} data-testid={`quote-status-select-${q.number}`}>
          {Object.entries(QUOTE_STATUS).map(([k, x]) => <option key={k} value={k}>{x.label}</option>)}
        </select>
      </div>
    </div>
  );
}

export default function AdminQuotes() {
  const [status, setStatus] = useState("");
  const [items, setItems] = useState(null);
  const load = useCallback(() => {
    setItems(null);
    api.get("/quotes", { params: { status } })
      .then(({ data }) => setItems(data))
      .catch((e) => {
        toast.error(errMsg(e));
        setItems([]);
      });
  }, [status]);
  useEffect(() => {
    load();
  }, [load]);
  const onStatus = async (q, s) => {
    try {
      await api.patch(`/quotes/${q.id}/status`, { status: s });
      load();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <div>
      <div className="eyebrow mb-2">Perizie e forense</div>
      <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase mb-6" data-testid="admin-quotes-title">Richieste di preventivo</h1>
      <div className="flex flex-wrap gap-2 mb-8">
        {[["", "Tutte"], ...Object.entries(QUOTE_STATUS).map(([k, s]) => [k, s.label])].map(([k, l]) => (
          <button key={k || "all"} onClick={() => setStatus(k)} data-testid={`quote-filter-${k || "all"}`}
            className={`text-xs px-3 py-1.5 rounded-sm border transition-colors ${status === k ? "border-[#E50914] text-white bg-[#E50914]/15" : "border-[#383838] text-slate-400 hover:text-white"}`}>{l}</button>
        ))}
      </div>
      <div className="space-y-3" data-testid="quotes-list">
        {items?.length === 0 && <div className="surface p-10 text-center text-slate-500" data-testid="quotes-empty">Nessuna richiesta.</div>}
        {items?.map((q) => <QuoteCard key={q.id} q={q} onStatus={onStatus} />)}
      </div>
    </div>
  );
}
